import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";

/**
 * Footer — deep plum field with a gold hairline above. Address and contact
 * are set properly. A small "Staff Login" link lives in the bottom bar so
 * Nathan, Brittney and the CCC team can find the back office without
 * bookmarking /admin — kept modest (14px, muted) so it doesn't advertise
 * the door to organised opposition.
 */
export default function Footer() {
  const isES = (useLang()?.lang || "en") === "es";
  const year = new Date().getFullYear();

  const explore = isES
    ? [
        { to: "/about", label: "Acerca de" },
        { to: "/legislative-work", label: "Trabajo Legislativo" },
        { to: "/resources/statements", label: "Declaraciones" },
        { to: "/resources/bishop-letters", label: "Cartas de los Obispos" },
        { to: "/resources/voter-guide", label: "Guía del Votante" },
      ]
    : [
        { to: "/about", label: "About" },
        { to: "/legislative-work", label: "Legislative Work" },
        { to: "/resources/statements", label: "Statements & Testimony" },
        { to: "/resources/bishop-letters", label: "Bishops' Letters" },
        { to: "/resources/voter-guide", label: "Voter Guide" },
      ];

  const involved = isES
    ? [
        { to: "/take-action/action-alerts", label: "Alertas de Acción" },
        { to: "/take-action/join", label: "Unirse a la Red" },
        { to: "/take-action/events", label: "Eventos" },
        { to: "/take-action/find-my-legislator", label: "Encuentre a su Legislador" },
        { to: "/contact", label: "Contacto" },
        { to: "/jobs", label: "Empleo" },
      ]
    : [
        { to: "/take-action/action-alerts", label: "Action Alerts" },
        { to: "/take-action/join", label: "Join the Network" },
        { to: "/take-action/events", label: "Events" },
        { to: "/take-action/find-my-legislator", label: "Find My Legislator" },
        { to: "/contact", label: "Contact" },
        { to: "/jobs", label: "Jobs" },
      ];

  return (
    <footer
      className="bg-ccc-plum-deep text-ccc-paper mt-24"
      data-testid="site-footer"
    >
      {/* Gold hairline above — the "seal" line separating page from footer */}
      <div className="h-px bg-ccc-gold-light w-full" aria-hidden="true" />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 grid md:grid-cols-12 gap-10">
        {/* Wordmark + address */}
        <div className="md:col-span-5">
          <div
            className="font-serif font-bold text-ccc-gold-light mb-4"
            style={{ fontSize: "28px", lineHeight: 1.15 }}
          >
            Colorado Catholic Conference
          </div>
          <p
            className="text-ccc-paper/85 max-w-md"
            style={{ fontSize: "17px", lineHeight: 1.6 }}
          >
            {isES
              ? "La voz unida de los obispos católicos de Colorado en las políticas públicas y las relaciones gubernamentales."
              : "The united voice of the Catholic bishops of Colorado in public policy and government relations."}
          </p>
          <address
            className="mt-6 not-italic text-ccc-paper/70"
            style={{ fontSize: "15px", lineHeight: 1.7 }}
          >
            1535 Logan Street
            <br />
            Denver, CO 80203
            <br />
            <a
              href="mailto:info@cocatholic.org"
              className="underline decoration-ccc-gold-light underline-offset-4 hover:text-ccc-paper transition-colors"
            >
              info@cocatholic.org
            </a>
          </address>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow eyebrow-light mb-4">
            {isES ? "Explorar" : "Explore"}
          </div>
          <ul className="space-y-2">
            {explore.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="text-ccc-paper/80 hover:text-ccc-paper transition-colors"
                  style={{ fontSize: "17px" }}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow eyebrow-light mb-4">
            {isES ? "Participar" : "Get Involved"}
          </div>
          <ul className="space-y-2">
            {involved.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="text-ccc-paper/80 hover:text-ccc-paper transition-colors"
                  style={{ fontSize: "17px" }}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-ccc-paper/10">
        <div
          className="max-w-6xl mx-auto px-6 sm:px-10 py-6 flex flex-col md:flex-row items-center justify-between text-ccc-paper/60"
          style={{ fontSize: "14px" }}
        >
          <div>
            © {year} Colorado Catholic Conference.{" "}
            {isES ? "Todos los derechos reservados." : "All rights reserved."}
          </div>
          <div className="mt-3 md:mt-0">
            <Link
              to="/privacy"
              className="hover:text-ccc-paper transition-colors mr-6"
            >
              {isES ? "Privacidad" : "Privacy"}
            </Link>
            <Link to="/terms" className="hover:text-ccc-paper transition-colors mr-6">
              {isES ? "Términos" : "Terms"}
            </Link>
            <Link
              to="/admin/login"
              data-testid="footer-staff-login"
              className="hover:text-ccc-paper transition-colors"
            >
              {isES ? "Acceso del Personal" : "Staff Login"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
